import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Fingerprint, LogOut, ShieldHalf, Wallet, BookUser, Bell, Sparkles, Gift, ArrowLeftRight, Scale, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";

export default function Nav({ active }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = [
    { key: "wallet", label: "Wallet", icon: Wallet, path: "/dashboard", testid: "nav-wallet" },
    { key: "directory", label: "Directory", icon: BookUser, path: "/directory", testid: "nav-directory" },
    { key: "ledger", label: "Ledger", icon: Scale, path: "/ledger", testid: "nav-ledger" },
    ...(user?.role === "admin"
      ? [{ key: "admin", label: "Network Admin", icon: ShieldHalf, path: "/admin", testid: "nav-admin" }]
      : []),
  ];

  const loadNotifs = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setNotifs(res.data.items);
      setUnread(res.data.unread);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadNotifs();
    const handler = () => loadNotifs();
    window.addEventListener("signet-notify", handler);
    const iv = setInterval(loadNotifs, 20000);
    return () => {
      window.removeEventListener("signet-notify", handler);
      clearInterval(iv);
    };
  }, [loadNotifs]);

  const openNotifs = async (open) => {
    if (open && unread > 0) {
      try {
        await api.post("/notifications/read");
        setUnread(0);
      } catch (e) {}
    }
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-2xl bg-[#0A0A0C]/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3 sm:gap-8">
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            className="sm:hidden w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Menu"
          >
            {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
          <button
            data-testid="nav-logo"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2.5"
          >
            <div className="w-8 h-8 rounded-lg bg-[#00F0FF] flex items-center justify-center">
              <Fingerprint className="w-4.5 h-4.5 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-lg tracking-tighter">SIGNET</span>
          </button>

          <nav className="hidden sm:flex items-center gap-1">
            {navItems.map((it) => (
              <button
                key={it.key}
                data-testid={it.testid}
                onClick={() => navigate(it.path)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${active === it.key ? "bg-white/10 text-white" : "text-[#A0A0AB] hover:text-white"}`}
              >
                <it.icon className="w-4 h-4" /> {it.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <div data-testid="points-badge" className="hidden sm:flex items-center gap-1.5 rounded-full border border-[#FFE600]/30 bg-[#FFE600]/10 px-3 py-1.5">
            <Gift className="w-3.5 h-3.5 text-[#FFE600]" />
            <span className="font-mono-sig text-xs text-[#FFE600] font-semibold">{user?.reward_points ?? 0}</span>
            <span className="font-mono-sig text-[9px] tracking-wider uppercase text-[#FFE600]/70">pts</span>
          </div>

          <Popover onOpenChange={openNotifs}>
            <PopoverTrigger asChild>
              <button
                data-testid="notifications-button"
                className="relative w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-white hover:bg-white/10 transition-colors"
              >
                <Bell className="w-4 h-4" />
                {unread > 0 && (
                  <span data-testid="notif-unread" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF007F] text-white text-[10px] font-bold flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 bg-[#121216] border-white/10 text-white p-0 mr-2">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB]">Notifications</span>
              </div>
              <div className="max-h-80 overflow-y-auto" data-testid="notifications-list">
                {notifs.length === 0 ? (
                  <p className="text-center text-[#52525B] text-sm py-8">No notifications yet</p>
                ) : (
                  notifs.map((n) => (
                    <div key={n.id} className="px-4 py-3 border-b border-white/5 last:border-0 flex gap-3">
                      <div className={`mt-0.5 w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${n.type === "transfer_confirmed" ? "bg-[#FFE600]/15" : "bg-[#7A5CFF]/15"}`}>
                        {n.type === "transfer_confirmed" ? <Sparkles className="w-3.5 h-3.5 text-[#FFE600]" /> : <ArrowLeftRight className="w-3.5 h-3.5 text-[#7A5CFF]" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-white leading-snug">{n.message}</p>
                        <p className="font-mono-sig text-[10px] text-[#52525B] mt-1">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>

          <div className="hidden sm:flex flex-col items-end">
            <span className="text-sm font-medium leading-tight">{user?.name}</span>
            <span className="font-mono-sig text-[10px] tracking-wider text-[#52525B] uppercase">{user?.role}</span>
          </div>
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-white/20 object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]}
            </div>
          )}
          <motion.button
            data-testid="logout-button"
            whileTap={{ scale: 0.92 }}
            onClick={logout}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-white hover:bg-white/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="sm:hidden overflow-hidden border-t border-white/10 bg-[#0A0A0C]/95"
          >
            <div className="px-5 py-3 flex flex-col gap-1">
              {navItems.map((it) => (
                <button
                  key={it.key}
                  data-testid={`${it.testid}-mobile`}
                  onClick={() => { setMenuOpen(false); navigate(it.path); }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${active === it.key ? "bg-white/10 text-white" : "text-[#A0A0AB] hover:text-white hover:bg-white/5"}`}
                >
                  <it.icon className="w-4 h-4" /> {it.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
